import {createPortal} from 'react-dom'
import {AlertTriangle, Loader2, X} from 'lucide-react'
import {useEffect, useId, useRef, useState} from 'react'
import useDialogAccessibility from './useDialogAccessibility'

interface ConfirmDialogProps {
  isOpen: boolean
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'danger' | 'warning'
  onConfirm: () => void | Promise<unknown>
  onCancel: () => void
}

export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  variant = 'danger',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')
  const pendingRef = useRef(false)
  const titleId = useId()
  const messageId = useId()
  const dialogRef = useDialogAccessibility<HTMLDivElement>(isOpen, onCancel, pending)
  useEffect(() => {
    if (isOpen) setError('')
  }, [isOpen])

  const handleConfirm = async () => {
    if (pendingRef.current) return
    pendingRef.current = true
    setPending(true)
    setError('')
    try {
      await onConfirm()
    } catch {
      setError('Não foi possível concluir a ação. Tente novamente.')
    } finally {
      pendingRef.current = false
      setPending(false)
    }
  }
  if (!isOpen) return null
  const isDanger = variant === 'danger'

  return createPortal(
    <div className='devops-modal-root fixed inset-0 z-[1400] flex items-center justify-center p-4 animate-fade-in'>
      <div
        className='fixed inset-0 bg-slate-900/50 backdrop-blur-sm'
        onClick={() => !pending && onCancel()}
        aria-hidden='true'
      />
      <div
        ref={dialogRef}
        role='alertdialog'
        aria-modal='true'
        aria-labelledby={titleId}
        aria-describedby={messageId}
        aria-busy={pending}
        tabIndex={-1}
        className='devops-surface relative w-full max-w-md max-h-[90dvh] overflow-y-auto animate-scale-in p-6 outline-none'
      >
        <button
          type='button'
          onClick={onCancel}
          disabled={pending}
          className='absolute top-4 right-4 devops-icon-button disabled:opacity-40'
          aria-label='Fechar confirmação'
        >
          <X size={18} />
        </button>
        <div className='flex items-start gap-4 mb-4 pr-5'>
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
              isDanger ? 'bg-red-50' : 'bg-amber-50'
            }`}
          >
            <AlertTriangle size={20} className={isDanger ? 'text-red-500' : 'text-amber-500'} />
          </div>
          <div>
            <h3 id={titleId} className='text-base font-semibold text-slate-800'>
              {title}
            </h3>
            <p id={messageId} className='text-sm text-slate-500 mt-1 leading-relaxed break-words'>
              {message}
            </p>
          </div>
        </div>
        {error && (
          <p role='alert' className='rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700'>
            {error}
          </p>
        )}
        <div className='flex gap-3 mt-6'>
          <button
            type='button'
            onClick={onCancel}
            disabled={pending}
            data-dialog-autofocus
            className='devops-secondary-button flex-1 disabled:opacity-40'
          >
            {cancelLabel}
          </button>
          <button
            type='button'
            onClick={handleConfirm}
            disabled={pending}
            className={`flex flex-1 items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white transition-colors disabled:opacity-60 ${
              isDanger ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'
            }`}
          >
            {pending && <Loader2 size={16} className='animate-spin' />}
            {pending ? 'Aguarde...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
